import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Typography, IconButton, Select, MenuItem } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SectionLabel from 'features/IRWorkstation/components/SectionLabel';
import { OUTREACH_TOPICS } from 'features/IRWorkstation/constants/outreachTopics';
import useGenerateDraft from 'hooks/useGenerateDraft';
import { useNotificationContext } from 'contexts/NotificationContext';
import PrimaryButton from 'features/IRWorkstation/components/Common/Buttons/PrimaryButton';
import QuickEditsForm from './QuickEditsForm';
import SourcedRecordsSection from 'features/IRWorkstation/components/Alerts/AlertsDrawer/SourcedRecordsSection';

const EmailField = ({ label, value }) => {
	if (!value) return null;

	return (
		<Box
			sx={{
				display: 'flex',
				gap: '10px',
				py: '2px',
				alignItems: 'flex-start',
			}}
		>
			<Typography
				sx={{
					fontSize: '12px',
					color: 'text.secondary',
					width: 56,
					flexShrink: 0,
				}}
			>
				{label}
			</Typography>
			<Typography sx={{ fontSize: '12px', color: 'text.primary' }}>
				{value}
			</Typography>
		</Box>
	);
};

EmailField.propTypes = {
	label: PropTypes.string.isRequired,
	value: PropTypes.string,
};

/**
 * Lets the user pick an outreach topic and generate an AI-drafted LP email for
 * the record an alert points at. Rendered only for alerts whose Salesforce
 * object the drafting endpoint supports - see resolveDraftOutreachTarget in
 * AlertsDrawer.
 */
const DraftOutreachSection = ({
	tenantId,
	salesforceObjectType,
	salesforceObjectId,
	salesforceOrgUrl,
	onGenerateClick,
	onApplyClick,
	onViewInCrmClick,
	onCopyClick,
	showSectionLabel = true,
}) => {
	const [topic, setTopic] = useState('');

	const { generate, apply, reset, draft, generateLoading, applyEditsLoading } = useGenerateDraft();
	const { showNotification } = useNotificationContext();

	// Control disabled state on form components without affecting button loading states
	const disabled = generateLoading || applyEditsLoading;
	
	// Drop any existing draft: it was generated for the previous topic, and
	// leaving it on screen would misrepresent what the dropdown now says.
	const handleTopicChange = (event) => {
		setTopic(event.target.value);
		reset();
	};
	
	const handleGenerate = async () => {
		try {
			onGenerateClick?.();
			await generate({
				tenantId,
				topic,
				salesforceObjectType,
				salesforceObjectId,
			});
		} catch (error) {
			// The hook surfaces the service's user-safe message.
			showNotification(
				error.message || 'Failed to generate outreach draft',
				'error'
			);
		}
	};

	const recipient = draft?.contact?.email
		? `${draft.contact.name} <${draft.contact.email}>`
		: draft?.contact?.name;

	// Only reachable from the button inside the rendered card, so `draft` is set.
	const handleCopy = async () => {
		try {
			onCopyClick?.();
			await navigator.clipboard.writeText(
				`To: ${recipient}\nSubject: ${draft.email.subject}\n\n${draft.email.body}`
			);
			showNotification('Email copied', 'success');
		} catch (error) {
			console.error('Failed to copy email:', error);
			showNotification('Failed to copy email', 'error');
		}
	};

	const handleApplyEdits = async ({ presetTypes, customInstruction }) => {
		try {
			onApplyClick?.();
			await apply({ tenantId, presetTypes, customInstruction });
			return true;
		} catch (error) {
			showNotification(error.message || 'Failed to apply requested changes', 'error');
			return false;
		}
	};

	return (
		<Box>
			{showSectionLabel && <SectionLabel text={'Draft Outreach'} />}

			<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
				<Select
					value={topic}
					onChange={handleTopicChange}
					displayEmpty
					size='small'
					disabled={disabled}
					sx={{ flex: 1, fontSize: '12px' }}
					inputProps={{ 'aria-label': 'Outreach type' }}
				>
					<MenuItem value='' disabled sx={{ fontSize: '12px' }}>
						Select outreach type
					</MenuItem>
					{OUTREACH_TOPICS.map((option) => (
						<MenuItem
							key={option.value}
							value={option.value}
							sx={{ fontSize: '12px' }}
						>
							{option.label}
						</MenuItem>
					))}
				</Select>

				<PrimaryButton
					onClick={handleGenerate}
					loading={generateLoading}
					label={generateLoading ? 'Generating...' : 'Generate'}
					disabled={!topic || disabled}
				/>
			</Box>

			{draft?.email && (
				<Box>
					<Box
						sx={{
							position: 'relative',
							mt: 1,
							backgroundColor: 'background.paper',
							borderRadius: '8px',
							border: '0.5px solid',
							borderColor: 'border.light',
							overflow: 'hidden',
						}}
					>
						<IconButton
							data-testid='copy-outreach-email-button'
							aria-label='Copy email'
							size='small'
							onClick={handleCopy}
							sx={{
								position: 'absolute',
								top: 8,
								right: 8,
								width: 26,
								height: 26,
								borderRadius: '6px',
								border: '0.5px solid',
								borderColor: 'border.light',
								backgroundColor: 'background.paper',
								color: 'text.secondary',
								'&:hover': { backgroundColor: 'background.subtle' },
							}}
						>
							<ContentCopyIcon sx={{ fontSize: 14 }} />
						</IconButton>

						{/* To / Subject header strip */}
						<Box
							sx={{
								backgroundColor: 'background.paper',
								borderBottom: '0.5px solid',
								borderColor: 'border.light',
								padding: '8px 10px',
								pr: '44px',
							}}
						>
							<EmailField label={'To'} value={recipient} />
							<EmailField label={'Subject'} value={draft.email.subject} />
						</Box>

						{/* Email body */}
						<Box
							sx={{ backgroundColor: 'background.subtle', padding: '10px 12px' }}
						>
							<Typography
								sx={{
									fontSize: '12px',
									color: 'text.primary',
									whiteSpace: 'pre-wrap',
								}}
							>
								{draft.email.body}
							</Typography>
						</Box>
					</Box>
					
					{/* QUICK EDITS SECTION */}
					<QuickEditsForm
						key={draft.email?.body} // Remount form when draft changes
						handleApplyEdits={handleApplyEdits}
						disabled={disabled}
						loading={applyEditsLoading}
					/>

					{/* SOURCED RECORDS SECTION */}
					<SourcedRecordsSection
						context={draft.context}
						salesforceOrgUrl={salesforceOrgUrl}
						onViewInCrmClick={onViewInCrmClick}
					/>
				</Box>
			)}
		</Box>
	);
};

DraftOutreachSection.propTypes = {
	tenantId: PropTypes.string.isRequired,
	salesforceObjectType: PropTypes.string.isRequired,
	salesforceObjectId: PropTypes.string.isRequired,
	salesforceOrgUrl: PropTypes.string,
	onGenerateClick: PropTypes.func,
	onApplyClick: PropTypes.func,
	onViewInCrmClick: PropTypes.func,
	onCopyClick: PropTypes.func,
	showSectionLabel: PropTypes.bool,
};

export default DraftOutreachSection;
